import { type FormEvent, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { track } from '@/lib/analytics'
import { SOFT_LAUNCH } from '@/lib/launchMode'

const WORKER_URL = (import.meta.env.VITE_EARLY_ACCESS_WORKER_URL as string | undefined) ?? ''
const TURNSTILE_SITE_KEY =
  (import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined) ?? ''

type FormState = 'idle' | 'submitting' | 'success' | 'error' | 'missing-data-sources'

const DATA_SOURCE_KEYS = ['tabular', 'graph', 'vector', 'document', 'nosql', 'other'] as const
type DataSourceKey = (typeof DATA_SOURCE_KEYS)[number]

const ROLE_KEYS = [
  'aiEngineer',
  'agentDeveloper',
  'modelCreator',
  'dataScientist',
  'developer',
  'researcher',
  'business',
  'other',
] as const
type RoleKey = (typeof ROLE_KEYS)[number]

type TurnstileApi = {
  render: (
    el: string | HTMLElement,
    opts: {
      sitekey: string
      callback?: (token: string) => void
      'error-callback'?: () => void
      'expired-callback'?: () => void
      theme?: 'light' | 'dark' | 'auto'
    },
  ) => string
  remove: (id: string) => void
  reset: (id?: string) => void
}

declare global {
  interface Window {
    turnstile?: TurnstileApi
  }
}

const inputClass =
  'w-full px-3 py-2 rounded-md border border-mono-300 dark:border-mono-700 bg-mono-50 dark:bg-mono-900 text-lg focus:outline-none focus:ring-2 focus:ring-mono-400 dark:focus:ring-mono-600'

const labelClass = 'block text-base uppercase tracking-wider opacity-75 mb-1'

export function EarlyAccessForm() {
  const { t } = useTranslation()
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [company, setCompany] = useState('')
  const [role, setRole] = useState<RoleKey | ''>('')
  const [roleOther, setRoleOther] = useState('')
  const [useCase, setUseCase] = useState('')
  const [dataSources, setDataSources] = useState<DataSourceKey[]>([])
  const [dataSourcesOther, setDataSourcesOther] = useState('')
  const [optIn, setOptIn] = useState(false)
  const [turnstileToken, setTurnstileToken] = useState('')
  const [state, setState] = useState<FormState>('idle')

  const turnstileMountRef = useRef<HTMLDivElement | null>(null)
  const turnstileWidgetIdRef = useRef<string | null>(null)

  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return
    let cancelled = false
    let retries = 0
    const tryRender = () => {
      if (cancelled) return
      const mount = turnstileMountRef.current
      if (!mount) return
      if (!window.turnstile) {
        if (retries++ < 50) window.setTimeout(tryRender, 100)
        return
      }
      turnstileWidgetIdRef.current = window.turnstile.render(mount, {
        sitekey: TURNSTILE_SITE_KEY,
        callback: (token) => setTurnstileToken(token),
        'error-callback': () => setTurnstileToken(''),
        'expired-callback': () => setTurnstileToken(''),
      })
    }
    tryRender()
    return () => {
      cancelled = true
      const id = turnstileWidgetIdRef.current
      if (id && window.turnstile) {
        try {
          window.turnstile.remove(id)
        } catch {
          // ignore — widget may already be torn down
        }
      }
      turnstileWidgetIdRef.current = null
    }
  }, [])

  const resetTurnstile = () => {
    setTurnstileToken('')
    const id = turnstileWidgetIdRef.current
    if (id && window.turnstile) {
      try {
        window.turnstile.reset(id)
      } catch {
        // ignore
      }
    }
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (dataSources.length === 0) {
      setState('missing-data-sources')
      return
    }
    setState('submitting')
    try {
      const res = await fetch(WORKER_URL, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          first_name: firstName,
          last_name: lastName,
          work_email: email,
          company,
          role,
          role_other: roleOther,
          use_case: useCase,
          data_sources: dataSources,
          data_sources_other: dataSourcesOther,
          newsletter_opt_in: optIn,
          turnstile_token: turnstileToken,
        }),
      })
      track('early_access_submitted', { ok: res.ok, optedInToNewsletter: optIn })
      if (res.ok) {
        setState('success')
      } else {
        resetTurnstile()
        setState('error')
      }
    } catch {
      track('early_access_submitted', { ok: false, optedInToNewsletter: optIn })
      resetTurnstile()
      setState('error')
    }
  }

  function toggleDataSource(key: DataSourceKey, checked: boolean) {
    setDataSources((prev) => (checked ? [...prev, key] : prev.filter((k) => k !== key)))
  }

  if (state === 'success') {
    return (
      <div role="status" className="rounded-lg p-6 text-lg">
        {t('earlyAccess.success')}
      </div>
    )
  }

  const captchaRequired = Boolean(TURNSTILE_SITE_KEY)
  const submitDisabled =
    state === 'submitting' || (captchaRequired && !turnstileToken)

  return (
    <form onSubmit={onSubmit} className="rounded-lg p-6 space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="ea-first-name" className={labelClass}>
            {t('earlyAccess.firstNameLabel')}
          </label>
          <input
            id="ea-first-name"
            type="text"
            autoComplete="given-name"
            required
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            className={inputClass}
          />
        </div>

        <div>
          <label htmlFor="ea-last-name" className={labelClass}>
            {t('earlyAccess.lastNameLabel')}
          </label>
          <input
            id="ea-last-name"
            type="text"
            autoComplete="family-name"
            required
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            className={inputClass}
          />
        </div>
      </div>

      <div>
        <label htmlFor="ea-email" className={labelClass}>
          {t('earlyAccess.emailLabel')}
        </label>
        <input
          id="ea-email"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className={inputClass}
        />
      </div>

      <div>
        <label htmlFor="ea-company" className={labelClass}>
          {t('earlyAccess.companyLabel')}
        </label>
        <input
          id="ea-company"
          type="text"
          autoComplete="organization"
          required
          value={company}
          onChange={(e) => setCompany(e.target.value)}
          className={inputClass}
        />
      </div>

      <div>
        <label htmlFor="ea-role" className={labelClass}>
          {t('earlyAccess.roleLabel')}
        </label>
        <select
          id="ea-role"
          required
          value={role}
          onChange={(e) => setRole(e.target.value as RoleKey | '')}
          className={inputClass}
        >
          <option value="">{t('earlyAccess.rolePlaceholder')}</option>
          {ROLE_KEYS.map((key) => (
            <option key={key} value={key}>
              {t(`earlyAccess.roles.${key}`)}
            </option>
          ))}
        </select>
        {role === 'other' && (
          <input
            type="text"
            required
            value={roleOther}
            onChange={(e) => setRoleOther(e.target.value)}
            placeholder={t('earlyAccess.roleOtherPlaceholder')}
            className={`${inputClass} mt-3`}
          />
        )}
      </div>

      <div>
        <label htmlFor="ea-use-case" className={labelClass}>
          {t('earlyAccess.useCaseLabel')}
        </label>
        <p className="text-base opacity-60 -mt-1 mb-2">{t('earlyAccess.useCaseHelp')}</p>
        <textarea
          id="ea-use-case"
          rows={3}
          required
          value={useCase}
          onChange={(e) => setUseCase(e.target.value)}
          className={inputClass}
        />
      </div>

      <fieldset>
        <legend className={labelClass}>
          {t('earlyAccess.dataSourcesLabel')}{' '}
          <span aria-hidden="true" className="text-red-600 dark:text-red-400">
            *
          </span>
        </legend>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-1">
          {DATA_SOURCE_KEYS.map((key) => (
            <label key={key} className="flex items-center gap-2 text-base cursor-pointer">
              <input
                type="checkbox"
                checked={dataSources.includes(key)}
                onChange={(e) => toggleDataSource(key, e.target.checked)}
                className="h-4 w-4 rounded border-mono-300 dark:border-mono-700 accent-mono-800 dark:accent-mono-100"
              />
              {t(`earlyAccess.dataSources.${key}`)}
            </label>
          ))}
        </div>
        {state === 'missing-data-sources' && (
          <p className="text-base text-red-600 dark:text-red-400 mt-2">
            {t('earlyAccess.dataSourcesRequired')}
          </p>
        )}
        {dataSources.includes('other') && (
          <input
            type="text"
            required
            value={dataSourcesOther}
            onChange={(e) => setDataSourcesOther(e.target.value)}
            placeholder={t('earlyAccess.dataSourcesOtherPlaceholder')}
            className={`${inputClass} mt-3`}
          />
        )}
      </fieldset>

      {!SOFT_LAUNCH && (
        <div className="flex items-center gap-2">
          <input
            id="ea-opt-in"
            type="checkbox"
            checked={optIn}
            onChange={(e) => setOptIn(e.target.checked)}
            className="h-4 w-4 rounded border-mono-300 dark:border-mono-700 accent-mono-800 dark:accent-mono-100"
          />
          <label htmlFor="ea-opt-in" className="text-base opacity-75 cursor-pointer">
            {t('earlyAccess.optInLabel')}
          </label>
        </div>
      )}

      {captchaRequired && <div ref={turnstileMountRef} className="cf-turnstile-mount" />}

      {state === 'error' && (
        <p className="text-base text-red-600 dark:text-red-400">{t('earlyAccess.error')}</p>
      )}

      <button
        type="submit"
        disabled={submitDisabled}
        className="pr-5 py-2.5 rounded-md text-base font-medium bg-mono-800 text-mono-100 dark:bg-mono-100 dark:text-mono-900 marker-dot disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {t('earlyAccess.submit')}
      </button>
    </form>
  )
}
